import { CaretRightOutlined } from '@ant-design/icons'
import { Timeline } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { PostTypeIcon } from '@/components'
import { PostType, UserTimelineActionType } from '@/generated/graphql'
import { AnniversaryCardContainer } from '@/modules/anniversary-card/anniversary-card.container'
import { routes } from '@/routes'

import { useIsViewerUserId } from '../user.hooks'
import { TimelineItemCard, UserTimelineItemProps } from './user-timeline-item-card.component'
import s from './user-timeline.module.less'

export const TimelineItemDescription: React.FC<UserTimelineItemProps> = ({ event, showSimplified }) => {
  const [t] = useTranslation()

  if (!event?.entity?.__typename) {
    return null
  }

  const description = (() => {
    if (event.entity.__typename === 'Post' && event.actionType === UserTimelineActionType.PostCreate) {
      switch (event.entity.postType) {
        case PostType.Thanks:
          return t('timeline.createThanks')
        case PostType.Forum:
          return t('timeline.createForum')
        case PostType.Journal:
          return t('timeline.createJournal')
        case PostType.Honor:
          return t('timeline.createHonor')
        case PostType.Event:
          return t('timeline.createEvent')
        case PostType.General:
          return t('timeline.createPost')
        case PostType.Announcement:
          return t('timeline.createAnnouncement')
        case PostType.Introduction:
          return t('timeline.createIntroduction')
      }
    }
    switch (event.actionType) {
      case UserTimelineActionType.PostThankReceive: {
        return t('timeline.receiveThanks')
      }
      case UserTimelineActionType.PostEventParticipate: {
        return t('timeline.participateEvent')
      }
      case UserTimelineActionType.PostEventInterest: {
        return t('timeline.interestedEvent')
      }
      case UserTimelineActionType.PostEventInvited: {
        return t('timeline.invitedEventGuest')
      }
      case UserTimelineActionType.PostHonorParticipate: {
        return t('timeline.contribute')
      }
      case UserTimelineActionType.PostHonorSignificant: {
        return t('timeline.highlightedContribute')
      }
      case UserTimelineActionType.AnniversaryReceive: {
        return moment(event.entity.createdAt).year() === moment(event.entity['hireDate']).year()
          ? t('timeline.anniversaryEvent.first')
          : t('timeline.anniversaryEvent.after', {
              yearText: Number(event.entity['anniversary']) - moment(event.entity['hireDate']).year(),
            })
      }
    }
  })()

  return event.entity.__typename === 'Post' ? (
    <>
      <span>
        {showSimplified ? description : <Link to={routes.posts.page(event.entity.postId)}>{description}</Link>}
      </span>
      {!showSimplified && (
        <>
          <CaretRightOutlined />
          <span>
            {event.entity.group ? (
              <Link to={routes.groups.page(event.entity.group.groupId)}>{event.entity.group.name}</Link>
            ) : (
              t('timeline.title')
            )}
          </span>
        </>
      )}
    </>
  ) : (
    <span>{description}</span>
  )
}

export const UserTimelineItem: React.FC<
  UserTimelineItemProps & {
    userId: number
    setTimelineItemType: React.Dispatch<React.SetStateAction<PostType | 'AnniversaryCard' | 'All'>>
  }
> = ({ userId, event, showSimplified, setTimelineItemType }) => {
  const isViewer = useIsViewerUserId(userId)
  const [visible, setVisible] = React.useState(false)
  if (!event?.entity?.__typename) {
    return null
  }

  const content = (() => {
    switch (event.entity.__typename) {
      case 'AnniversaryCard': {
        const anniversaryCard = event.entity
        const isZeroYear = Number(anniversaryCard.anniversary) === moment(anniversaryCard['hireDate']).year()
        return (
          <Timeline.Item
            dot={
              <span
                onClick={() =>
                  setTimelineItemType((previous) => {
                    if (previous !== 'AnniversaryCard') {
                      return 'AnniversaryCard'
                    }
                    return 'All'
                  })
                }
                style={{ fontSize: 18 }}
              >
                🥳
              </span>
            }
          >
            <span
              style={{ cursor: isViewer && !isZeroYear ? 'pointer' : undefined }}
              onClick={() => isViewer && setVisible(true)}
              className={s.itemDescription}
            >
              {moment(anniversaryCard.hireDate).format('MM-DD')}
              <TimelineItemDescription event={event} />
            </span>
            {!isZeroYear && (
              <AnniversaryCardContainer
                anniversary={anniversaryCard.anniversary}
                setViewMode={setVisible}
                isViewMode={visible}
              />
            )}
          </Timeline.Item>
        )
      }
      case 'Post': {
        const generalIcon = [PostType.Introduction, PostType.General, PostType.Announcement]
        const isHonor = event.entity.postType === PostType.Honor
        return (
          <Timeline.Item
            dot={
              <div className={s.itemHead}>
                <PostTypeIcon
                  onClick={(type: any) =>
                    setTimelineItemType((previous) => {
                      if (previous !== type) {
                        return type
                      }
                      return 'All'
                    })
                  }
                  style={{ color: isHonor && '#ffa03a' }}
                  type={generalIcon.includes(event.entity.postType) ? PostType.General : event.entity.postType}
                />
              </div>
            }
          >
            <span className={s.itemDescription}>
              {moment(event.createdAt).format('MM-DD')}
              <TimelineItemDescription showSimplified={showSimplified} event={event} />
            </span>
            <TimelineItemCard event={event!} isSimplified={showSimplified} />
          </Timeline.Item>
        )
      }
    }
  })()

  return content
}
